import React from "react"
import {useNavigate} from "react-router-dom"
import TitleBar, {MetaItemProps, TitleBarAction} from "../../components/TitleBar/TitleBar"
import {MainSection} from "../../containers/MainSection/MainSection"
import {
	ApplicationDocument,
	ApplicationType,
	ApplicationTypeIcons,
	isApplicationDocument,
} from "../../resources/application"
import {getProductApplicationActions} from "../../resources/applicationActions"
import {
	AssigneeTag,
	CreatedAtTag,
	UpdatedAtTag,
	EvaluationOutcomeTag,
	ProductApplicationStatusTag,
	Tags,
} from "../../components/Tag"
import {OkDocument, Resource} from "../../resources/common"
import {getAuditLogEventsHeaderMetaItems} from "../Applications/auditLogMetaItems"
import {isOrg, Org} from "../../resources/org"
import {useAuditLog} from "../../components/AuditLogDrawer/useAuditLog"
import AuditLogActionButton from "../../components/AuditLogActionButton/AuditLogActionButton"
import AuditLogDrawer from "../../components/AuditLogDrawer/AuditLogDrawer"
import {
	getProductApplicationName,
	IndividualProductApplication,
	individualProductApplicationPreApprovedLink,
	ProductApplicationStatus,
	reevaluableProductApplicationStatuses,
} from "../../resources/productApplication"
import ApplicationDocuments from "../../components/ApplicationDocuments/ApplicationDocuments"
import ProductApplicationAdditionalInfo from "../../components/ProductApplicationAdditionalInfo/ProductApplicationAdditionalInfo"
import InformationIndividualProductApplication from "../../components/IndividualInformationProductApplication/InformationIndividualProductApplication"
import EvaluationFlagsIndividualProductApplication from "../../components/EvaluationFlagsIndividualProductApplication/EvaluationFlagsIndividualProductApplication"
import {
	useAccessToken,
	useIsUnitComplianceUser,
	useIsBankOperationsUser,
	useIsBankReadOnlyUser,
} from "../../services/auth"
import {useModal} from "react-modal-hook"
import Button from "../../components/Button/Button"
import LinkButton from "../../components/LinkButton/LinkButton"
import {HasPermission} from "../../containers/PermissionedUser/Permission"
import Icon from "../../components/Icon/Icon"
import {ApproveProductApplicationModal} from "../../components/ApplicationApprove/ProductApplicationApprove"
import {CancelProductApplicationModal} from "../../components/ApplicationCancel/ProductApplicationCancel"
import {DenyProductApplicationModal} from "../../components/ApplicationDeny/ProductApplicationDeny"
import {ProductIndividualRequestDocumentModal} from "../../components/RequestDocumentModal/ProductApplicationRequestDocumentModal"
import TagsCard from "../../components/Tags/TagsCard"
import {useListReturnPath} from "../../hooks/useListReturnPath"
import {ProductApplicationDecision} from "../../components/ApplicationDecision/ProductApplicationDecision"
import {CompletePendingModal} from "../../components/ApplicationCompletePending/ApplicationCompletePendingModal"
import CompletePendingButton from "../../components/ApplicationCompletePendingButton/CompletePendingButton"
import {ProductApplicationAllowedToViewEvaluation} from "../../containers/PermissionedUser/PermissionedUser"

function IndividualProductApplicationInternal({
	individual,
	refresh,
	documents,
	included,
}: {
	individual: IndividualProductApplication
	refresh: () => void
	documents: ApplicationDocument[]
	included?: Resource[]
}) {
	const listReturnPath = useListReturnPath("/applications/#tab=productApplication")
	const {attributes} = individual
	const navigate = useNavigate()
	const accessToken = useAccessToken()

	const canReevaluate = reevaluableProductApplicationStatuses.includes(attributes.status)
	const handleResubmitNavigate = () => navigate(`/product-applications/${individual.id}/resubmit`)

	const org = included?.find((r) => isOrg(r) && r.id == individual.relationships.org.data.id) as unknown as Org
	const name = getProductApplicationName(individual)
	const preApprovedProductApplicationLink = individualProductApplicationPreApprovedLink(individual)
	const {showAuditLog, openAuditLogDrawer, closeAuditLogDrawer, isAuditLogDrawerOpened} = useAuditLog()
	const auditLogEventsHeaderMetaItems = getAuditLogEventsHeaderMetaItems({
		applicationName: name,
		applicationType: ApplicationType.individual,
		pageIcon: ApplicationTypeIcons[ApplicationType.individual],
		org: org,
	})
	const defaultInformationHidingState =
		!useIsUnitComplianceUser() && !useIsBankOperationsUser() && !useIsBankReadOnlyUser()

	const {canApprove, canDeny, canCancel} = getProductApplicationActions(attributes.status)

	const [showApprove, hideApprove] = useModal(() => (
		<ApproveProductApplicationModal close={hideApprove} application={individual} onSuccess={refresh} />
	))

	const [showDeny, hideDeny] = useModal(() => (
		<DenyProductApplicationModal
			close={hideDeny}
			application={individual}
			onSuccess={refresh}
			accessToken={accessToken}
			resources={included}
		/>
	))

	const [showCancel, hideCancel] = useModal(() => (
		<CancelProductApplicationModal close={hideCancel} application={individual} onSuccess={refresh} />
	))
	const [showRequestDocument, hideRequestDocument] = useModal(() => (
		<ProductIndividualRequestDocumentModal application={individual} close={hideRequestDocument} onSuccess={refresh} />
	))
	const [showCompletePending, hideCompletePending] = useModal(() => (
		<CompletePendingModal close={hideCompletePending} application={individual} onSuccess={refresh} />
	))

	const customerLink = `/customers/individual/${individual.relationships.customer.data.id}`
	const metaTitleBar = [
		{text: "Individual", icon: "user-geometric-action---users"},
		{
			text: "Customer",
			icon: "user-geometric-action-money-fund---users",
			path: customerLink,
		},
	] as MetaItemProps[]

	return (
		<>
			<TitleBar title={name} breadcrumbs={[{text: "Product Applications", path: listReturnPath}]} meta={metaTitleBar}>
				{showAuditLog && <AuditLogActionButton onClick={openAuditLogDrawer} />}
				<HasPermission resource="productApplication" action="update">
					<TitleBarAction>
						<Button
							tooltipPlace="bottom"
							disabled={!canReevaluate}
							theme="neutral"
							variant="outline"
							onClick={handleResubmitNavigate}
							prefixIcon="pencil-1--interface-essential"
							className="mr-2"
						>
							<span>Edit and Re-submit</span>
						</Button>
					</TitleBarAction>
				</HasPermission>
				<TitleBarAction>
					<HasPermission resource="productApplication.status.cancel" action="update">
						{canCancel && (
							<button className="button button-cancel mr-0 is-flex is-align-items-center" onClick={showCancel}>
								<Icon icon={"user-geometric-action-cross-remove"} size={16} />
								<span>Cancel Application</span>
							</button>
						)}
					</HasPermission>
				</TitleBarAction>
				<TitleBarAction>
					<CompletePendingButton application={individual} buttonOnClick={showCompletePending} />
				</TitleBarAction>
			</TitleBar>

			<MainSection>
				<AuditLogDrawer
					open={isAuditLogDrawerOpened}
					onClose={closeAuditLogDrawer}
					auditLogEventsHeaderMetaItems={auditLogEventsHeaderMetaItems}
					resourceType="productApplication"
					resourceId={individual.id}
					showSubResourcesId={true}
				/>
				<div className="columns is-mobile">
					<div className="column is-narrow">
						<div className="buttons">
							{attributes.evaluationEntityId ? (
								<LinkButton to={`https://unit.app.alloy.com/entities/${attributes.evaluationEntityId}`}>
									Alloy
								</LinkButton>
							) : null}
						</div>
					</div>
					<div className="column">
						<HasPermission resource="productApplication.status" action="update">
							<div className="buttons is-right">
								{attributes.status === ProductApplicationStatus.AwaitingDocuments ||
								attributes.status === ProductApplicationStatus.PendingReview ? (
									<button className="button is-info" onClick={() => showRequestDocument()}>
										Request Documents
									</button>
								) : null}
								<button className="button is-danger" disabled={!canDeny} onClick={() => showDeny()}>
									Deny
								</button>

								<button className="button is-success" disabled={!canApprove} onClick={() => showApprove()}>
									Approve
								</button>
							</div>
						</HasPermission>
					</div>
				</div>
				<div className="columns">
					<div className="column">
						<Tags>
							<CreatedAtTag createdAt={attributes.createdAt} />
							<UpdatedAtTag updatedAt={attributes.updatedAt} />
							{attributes.assignee ? <AssigneeTag assignee={attributes.assignee} /> : <React.Fragment />}
							<ProductApplicationStatusTag status={attributes.status} />
							<ProductApplicationAllowedToViewEvaluation>
								{attributes.evaluationOutcome && (
									<EvaluationOutcomeTag value={attributes.evaluationOutcome} label="Individual Evaluation" />
								)}
							</ProductApplicationAllowedToViewEvaluation>
						</Tags>
					</div>
				</div>
				<div className="columns">
					<div className="column is-4">
						<ProductApplicationDecision application={individual} />
						<div className="columns">
							<div className="column">
								<ProductApplicationAdditionalInfo
									requestedProduct={attributes.requestedProduct}
									decisionMethod={attributes.decisionMethod}
									preApprovedEvaluatedAt={attributes.preApprovedEvaluatedAt}
									orgId={individual.relationships.org.data.id}
									included={included}
									productApplicationPreApprovedLink={preApprovedProductApplicationLink}
								/>
							</div>
						</div>

						<div className="columns">
							<div className="column">
								<InformationIndividualProductApplication
									individual={individual}
									defaultInformationHidingState={defaultInformationHidingState}
									included={included}
								/>
							</div>
						</div>
						<div className="columns">
							<div className="column">
								<TagsCard tags={attributes.tags} editable={false} />
							</div>
						</div>
					</div>
					<div className="column">
						<div className="columns">
							<div className="column">
								<EvaluationFlagsIndividualProductApplication application={individual} />
							</div>
						</div>
						<div className="columns">
							<div className="column">
								<ApplicationDocuments application={individual} documents={documents} refresh={refresh} />
							</div>
						</div>
					</div>
				</div>
			</MainSection>
		</>
	)
}

export default function IndividualProductApplicationPage({
	productApplicationDocument,
	refresh,
}: {
	productApplicationDocument: OkDocument<IndividualProductApplication>
	refresh: () => void
}) {
	return (
		<IndividualProductApplicationInternal
			individual={productApplicationDocument.data}
			refresh={refresh}
			documents={productApplicationDocument.included?.filter(isApplicationDocument) || []}
			included={productApplicationDocument.included}
		/>
	)
}
